import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { BlogPost } from "@/types";

// Extended BlogPost to match actual API response
interface ApiBlogPost extends Omit<BlogPost, "publishedAt"> {
  publishedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface BlogsResponse {
  blogs: ApiBlogPost[];
}

interface BlogDetailResponse {
  blog: ApiBlogPost;
  relatedBlogs: ApiBlogPost[];
}

interface LikeResponse {
  likes: number;
}

// Fetch all blogs
export function useBlogs() {
  return useQuery<BlogsResponse, Error>({
    queryKey: ["blogs"],
    queryFn: async () => {
      const response = await fetch("/api/blogs");
      if (!response.ok) {
        throw new Error("Failed to fetch blogs");
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Fetch single blog post by slug
export function useBlogDetail(slug: string) {
  return useQuery<BlogDetailResponse, Error>({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const response = await fetch(`/api/blogs/${slug}`);
      if (!response.ok) {
        throw new Error("Failed to fetch blog post");
      }
      return response.json();
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Like a blog post
export function useLikeBlog() {
  const queryClient = useQueryClient();

  return useMutation<
    LikeResponse,
    Error,
    { slug: string; blogId: string },
    { previousBlogs?: BlogsResponse; previousBlogDetail?: BlogDetailResponse }
  >({
    mutationFn: async ({ slug }) => {
      const response = await fetch(`/api/blogs/${slug}/like`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to like post");
      }

      return response.json();
    },
    onMutate: async ({ blogId, slug }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["blogs"] });
      await queryClient.cancelQueries({ queryKey: ["blog", slug] });

      // Snapshot the previous values
      const previousBlogs = queryClient.getQueryData<BlogsResponse>(["blogs"]);
      const previousBlogDetail = queryClient.getQueryData<BlogDetailResponse>([
        "blog",
        slug,
      ]);

      // Optimistically update blog list
      if (previousBlogs) {
        queryClient.setQueryData<BlogsResponse>(["blogs"], {
          blogs: previousBlogs.blogs.map((blog) =>
            blog._id === blogId ? { ...blog, likes: blog.likes + 1 } : blog
          ),
        });
      }

      // Optimistically update blog detail
      if (previousBlogDetail) {
        queryClient.setQueryData<BlogDetailResponse>(["blog", slug], {
          ...previousBlogDetail,
          blog: {
            ...previousBlogDetail.blog,
            likes: previousBlogDetail.blog.likes + 1,
          },
        });
      }

      return { previousBlogs, previousBlogDetail };
    },
    onSuccess: (data, { blogId, slug }) => {
      // Update with server response for blog list
      const previousBlogs = queryClient.getQueryData<BlogsResponse>(["blogs"]);
      if (previousBlogs) {
        queryClient.setQueryData<BlogsResponse>(["blogs"], {
          blogs: previousBlogs.blogs.map((blog) =>
            blog._id === blogId ? { ...blog, likes: data.likes } : blog
          ),
        });
      }

      // Update with server response for blog detail
      const previousBlogDetail = queryClient.getQueryData<BlogDetailResponse>([
        "blog",
        slug,
      ]);
      if (previousBlogDetail) {
        queryClient.setQueryData<BlogDetailResponse>(["blog", slug], {
          ...previousBlogDetail,
          blog: {
            ...previousBlogDetail.blog,
            likes: data.likes,
          },
        });
      }

      toast.success("Thanks for liking this post! ❤️");
    },
    onError: (_error, { slug }, context) => {
      // Rollback on error
      if (context?.previousBlogs) {
        queryClient.setQueryData(["blogs"], context.previousBlogs);
      }
      if (context?.previousBlogDetail) {
        queryClient.setQueryData(["blog", slug], context.previousBlogDetail);
      }
      toast.error("Failed to like post. Please try again.");
    },
  });
}
